"use client";

import { Trash2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { useOnlineStatus } from "@/app/hooks/useOnlineStatus";
import { ShareListSection } from "@/components/lists/ShareListSection";
import { ColorPicker } from "@/components/shared/ColorPicker";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Dialog } from "@/components/shared/Dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { SharedListInfo } from "@/lib/types";
import { listSchemas, validate } from "@/lib/validation";

interface ListModalProps {
  isOpen: boolean;
  isEditMode: boolean;
  listId?: string;
  listName: string;
  listColor: string;
  colorOptions: string[];
  isShared?: boolean; // True if this is a shared list (read-only for non-owners)
  hasShares?: boolean; // True if the owner has shared this list with others
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
  onNameChange: (name: string) => void;
  onColorChange: (color: string) => void;
  onUnshare?: () => void; // Callback when user unshares from a shared list
}

export function ListModal({
  isOpen,
  isEditMode,
  listId,
  listName,
  listColor,
  colorOptions,
  isShared = false,
  hasShares = false,
  onClose,
  onSave,
  onDelete,
  onNameChange,
  onColorChange,
  onUnshare,
}: ListModalProps) {
  const t = useTranslations("lists");
  const { isOffline } = useOnlineStatus();
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [shares, setShares] = useState<SharedListInfo[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [isShareOperationInProgress, setIsShareOperationInProgress] = useState(false);

  const fetchShares = useCallback(async () => {
    if (!listId) return;

    setLoadingShares(true);
    try {
      const response = await fetch(`/api/lists/${listId}/share`);
      if (response.ok) {
        const data = await response.json();
        setShares(
          data.shares.map((share: any) => ({
            email: share.email,
            accepted: share.accepted,
            invitationId: share.id,
            acceptedAt: share.acceptedAt,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to fetch shares:", error);
    } finally {
      setLoadingShares(false);
    }
  }, [listId]);

  useEffect(() => {
    if (!isOpen) {
      setShowDeleteConfirmDialog(false);
      setShares([]);
      setLoadingShares(false);
      setIsShareOperationInProgress(false);
    } else if (isOpen && isEditMode && listId && !isShared) {
      // Set loading state immediately before fetching, but only if we know the list has shares
      // This prevents showing skeleton unnecessarily for lists with no shares
      if (hasShares) {
        setLoadingShares(true);
      }
      // Load shares when opening edit modal for owned lists
      fetchShares();
    } else if (isOpen && !isShared) {
      // When opening add/edit modal, ensure focus moves to the input
      // This helps prevent aria-hidden focus conflicts
      setTimeout(() => {
        const input = document.querySelector('input[id="list-name"]') as HTMLInputElement;
        if (input && !input.disabled) {
          input.focus();
        }
      }, 100);
    }
  }, [isOpen, isEditMode, listId, isShared, hasShares, fetchShares]);

  const handleSave = () => {
    // Validate list name
    const nameValidation = validate(listSchemas.name, listName);
    if (!nameValidation.success) {
      const errorKey = nameValidation.error.replace("validation.", "");
      toast.error(t(errorKey as any));
      return false;
    }

    // Validate list color (only if provided)
    if (listColor) {
      const colorValidation = validate(listSchemas.color, listColor);
      if (!colorValidation.success) {
        const errorKey = colorValidation.error.replace("validation.", "");
        toast.error(t(errorKey as any));
        return false;
      }
    }

    onSave();
    return true;
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => {
        if (isShareOperationInProgress) return;
        if (isEditMode) {
          // Auto-save when editing existing lists
          const saved = handleSave();
          if (saved) {
            onClose();
          }
          return;
        }
        onClose();
      }}
      disableOutsideClick={isShareOperationInProgress}
      title={isShared ? t("sharedList") : isEditMode ? t("editList") : t("addNewList")}
      footer={
        !isEditMode && !isShared ? (
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={onClose} disabled={isShareOperationInProgress}>
              {t("cancel")}
            </Button>
            <Button onClick={handleSave} disabled={isShareOperationInProgress}>
              {t("addList")}
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="space-y-6">
        <div className="flex flex-col space-y-4">
          <Label htmlFor="list-name">{t("listName")}</Label>
          <Input
            id="list-name"
            value={listName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={t("enterListName")}
            onKeyDown={(e) => e.key === "Enter" && !isShareOperationInProgress && handleSave()}
            disabled={isShared || isShareOperationInProgress}
            maxLength={100}
          />
        </div>

        <div className="flex flex-col space-y-4">
          <Label>{t("listColor")}</Label>
          <ColorPicker
            colors={colorOptions}
            selected={listColor}
            onChange={onColorChange}
            disabled={isShared || isShareOperationInProgress}
          />
        </div>
      </div>

      {/* Share section for owned lists in edit mode */}
      {isEditMode && !isShared && listId && !isOffline && (
        <ShareListSection
          listId={listId}
          listName={listName}
          shares={shares}
          isLoadingShares={hasShares && loadingShares}
          onShareAdded={fetchShares}
          onShareRemoved={fetchShares}
          onLoadingChange={setIsShareOperationInProgress}
        />
      )}

      {/* Unshare option for shared lists */}
      {isShared && onUnshare && (
        <div className="border-t border-border pt-4 sm:pt-6">
          <Button
            variant="destructive"
            className="w-full"
            onClick={onUnshare}
            disabled={isShareOperationInProgress}
          >
            <Trash2 className="size-4" />
            {t("removeSharedList")}
          </Button>
        </div>
      )}

      {/* Delete section for owned lists */}
      {isEditMode && !isShared && onDelete && (
        <div className="border-t border-border pt-4 sm:pt-6">
          <Button
            variant="destructive"
            className="w-full"
            onClick={() => setShowDeleteConfirmDialog(true)}
            disabled={isShareOperationInProgress}
          >
            <Trash2 className="size-4" />
            {t("delete")}
          </Button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirmDialog}
        onClose={() => setShowDeleteConfirmDialog(false)}
        onConfirm={() => {
          onDelete?.();
          setShowDeleteConfirmDialog(false);
        }}
        title={t("deleteListTitle")}
        description={t("deleteListMessage")}
        confirmText={t("delete")}
        confirmIcon={<Trash2 className="size-4" />}
        cancelText={t("cancel")}
        variant="destructive"
      />
    </Dialog>
  );
}
